import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ASSET_PHOTO_MAX_FILE_SIZE_BYTES,
  EntityRefUtil,
  FORM_TYPE_LIMIT,
  GearbeltEvent,
  GenerateAssetPhotoPresignedUploadUrlRequestDTO,
  GetAssetByIdResponseDTO,
  getSchedulePlanLimit,
  FormType as GlobalFormType,
  RecordState,
  TagEntity,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { GearbeltAssetType, GearbeltPlanType } from '@site-mate/sitemate-global-shared/lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, isToday, startOfDay, subDays } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { match } from 'ts-pattern';
import { z } from 'zod';

import qrLogo from '@/assets/gearbelt-small-qrlogo-inverted.svg';
import { extendedAssetFormSchema } from '@/components/assets/asset-form';
import { QRPrintDocument } from '@/components/assets/qr-print-document';
import { FormBadge } from '@/components/form/form-badge';
import ArchiveIcon from '@/components/icons/archive';
import CameraIcon from '@/components/icons/camera';
import { CheckCircleIcon } from '@/components/icons/check-circle';
import { CloneIcon } from '@/components/icons/clone';
import DeleteIcon from '@/components/icons/delete';
import DownloadIcon from '@/components/icons/download';
import ExclamationIcon from '@/components/icons/exclamation';
import UndoIcon from '@/components/icons/undo';
import { WarningTriangleIcon } from '@/components/icons/warning-triangle';
import { ScheduleBadge } from '@/components/schedules/schedule-badge';
import { AsyncSelect } from '@/components/ui/async-select';
import { AsyncCreatableSelect } from '@/components/ui/creatable-select';
import { Dropzone, DropzoneContent, DropzoneDescription, DropzoneInput, DropzoneTitle } from '@/components/ui/dropzone';
import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AsyncCreatableMultiSelect, MULTI_SELECT_STYLES } from '@/components/ui/multi-select';
import { Select, SELECT_STYLES } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { usePermissions } from '@/hooks/use-permissions';
import { useAssetQRCodePrint } from '@/hooks/useAssetQRCodePrint';
import { loadUserOptions, type UserOption } from '@/lib/user-options';
import { cn, initials } from '@/lib/utils';
import { FormType } from '@/models/api';
import { getSitemateStartAccountQueryOptions } from '@/queries/account';
import { assetCategoryKeys, getAssetCategoriesQueryOptions } from '@/queries/asset-categories';
import { assetKeys, getAssetByIdQueryOptions, getManufacturersQueryOptions, getTagsQueryOptions } from '@/queries/assets';
import { getLocationsQueryOptions } from '@/queries/locations';
import { getUnitsQueryOptions } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAssetQrUrl } from '@/utils/asset';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { getProcessedForms, getProcessedSchedules, getScheduleLimitWarning, getUniqueFormsByType, ProcessedSchedule } from '@/utils/deployment-rules';
import { useEvents } from '@/utils/events';
import { getWorkspaceRoute, useWorkspaceNavigate } from '@/utils/routes';
import { postToPresignedTarget } from '@/utils/upload';

export function AssetOverviewPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useWorkspaceNavigate();
  const { id } = useParams();
  const { trackEvent } = useEvents();
  const { org } = useUserStore();
  const { canManageAssets } = usePermissions();
  const { data: asset, isLoading: assetLoading } = useQuery(getAssetByIdQueryOptions({ assetId: id }));
  const { data: locationsData, isLoading: locationsLoading } = useQuery(getLocationsQueryOptions());
  const locations = locationsData?.data;

  const { data: manufacturers, isLoading: manufacturersLoading } = useQuery(getManufacturersQueryOptions());

  const { data: assetCategories, isLoading: assetCategoriesLoading } = useQuery(getAssetCategoriesQueryOptions());
  const { data: tags, isLoading: tagsLoading } = useQuery(getTagsQueryOptions({ entity: TagEntity.Asset }));
  const { data: units, isLoading: unitsLoading } = useQuery(getUnitsQueryOptions());

  const { handlePrint: handleQRCodeDownload, printQrRef, assetQRCodes } = useAssetQRCodePrint(asset ? [asset] : []);

  const assetQrUrl = asset?.qrCodes?.[0]?.value ? getAssetQrUrl(asset.qrCodes[0].value) : null;

  const handleCopyAssetUrl = async () => {
    if (!assetQrUrl) return;
    try {
      await navigator.clipboard.writeText(assetQrUrl);
      toast.success('Asset URL copied to clipboard');
    } catch {
      toast.error('Failed to copy the Asset URL. Please try again.');
    }
  };

  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const hasTrackedEventRef = useRef<string | null>(null);

  useEffect(() => {
    if (id && hasTrackedEventRef.current !== id) {
      hasTrackedEventRef.current = id;
      trackEvent(GearbeltEvent.AssetProfileSelected);
    }
  }, [id, trackEvent]);

  // Preload image when asset changes
  useEffect(() => {
    if (asset?.thumbnailUrl) {
      let isCancelled = false;
      const img = new Image();

      const handleLoad = () => {
        if (!isCancelled) setIsImageLoaded(true);
      };

      img.onload = handleLoad;
      img.onerror = handleLoad;
      img.src = asset.thumbnailUrl;

      return () => {
        isCancelled = true;
        img.onload = null;
        img.onerror = null;
      };
    } else {
      // If no thumbnail, no need to wait for image loading
      setIsImageLoaded(true);
    }
  }, [asset?.thumbnailUrl]);

  const isLoading = useMemo(
    () => assetLoading || locationsLoading || manufacturersLoading || assetCategoriesLoading || tagsLoading || unitsLoading,
    [assetLoading, locationsLoading, manufacturersLoading, assetCategoriesLoading, tagsLoading, unitsLoading],
  );

  const { mutateAsync: updateAsset, isPending: updateAssetPending } = useMutation({
    mutationFn: (values: z.infer<typeof extendedAssetFormSchema>) => {
      if (!id) return Promise.reject(new Error('Asset ID is required'));
      return ApiService.UpdateAsset(id, {
        internalId: values.internalId,
        type: values.type,
        chassisVin: values.vin,
        class: values.class,
        engineNumber: values.engineNumber,
        manufacturer: values.manufacturer,
        model: values.model,
        registration: values.registration,
        serialNumber: values.serialNumber,
        year: values.year ? Number(values.year) : undefined,
        categoryId: values.categoryId || null,
        tags: values.tags?.map(({ value }) => value) || [],
        comments: values.comments,
        locationId: values.locationId || null,
        operatedById: values.operatedBy?.value ?? null,
        rate: values.rate ? Number(values.rate) : null,
      });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: assetKeys.detail(id, false),
      });
      toast.success('Asset updated successfully');
    },
    onError: (error) => {
      toast.error(`Error updating asset: ${error.message}`);
    },
  });

  const { mutateAsync: createAssetCategory, isPending: createAssetCategoryPending } = useMutation({
    mutationFn: async (values: z.infer<typeof extendedAssetFormSchema>) => {
      if (!values.categoryId) throw new Error('Category is required');
      return await (
        await ApiService.CreateWorkspaceAssetCategory({
          name: values.categoryId,
          assetType: values.type,
        })
      ).json();
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: assetCategoryKeys.all,
      });
    },
    onError: (error) => {
      toast.error(`Error adding asset category: ${error.message}`);
    },
  });

  const { mutateAsync: bulkUpdateAssetState, isPending: isUpdatingAssetState } = useMutation({
    mutationFn: ({ state }: { state: RecordState }) => {
      if (!id) return Promise.reject(new Error('Asset ID is required'));
      return ApiService.BulkUpdateAssetState({ ids: [id], state });
    },
    onSuccess: async (_, { state }) => {
      const messages = {
        [RecordState.Active]: 'Asset restored successfully',
        [RecordState.Archived]: 'Asset archived successfully',
        [RecordState.Deleted]: 'Asset deleted successfully',
      };
      toast.success(messages[state]);
      if (state === RecordState.Deleted) {
        navigate('/assets');
      }
      queryClient.invalidateQueries({
        queryKey: assetKeys.detail(id, false),
      });
    },
    onError: (error, { state }) => {
      const actions = {
        [RecordState.Active]: 'restoring',
        [RecordState.Archived]: 'archiving',
        [RecordState.Deleted]: 'deleting',
      };
      toast.error(`Error ${actions[state]} asset: ${error.message}`);
    },
  });

  const { mutateAsync: generateAssetPhotoPresignedUploadUrl } = useMutation({
    mutationFn: async (dto: GenerateAssetPhotoPresignedUploadUrlRequestDTO) => {
      return await (await ApiService.GenerateAssetPhotoPresignedUploadUrl(dto)).json();
    },
  });

  const { mutateAsync: updateAssetPhoto, isPending: setAssetPhotoPending } = useMutation({
    mutationFn: async ({ photoUrl, thumbnailUrl }: { photoUrl: string | null; thumbnailUrl: string | null }) => {
      if (!id) return Promise.reject(new Error('Asset ID is required'));
      return await (await ApiService.UpdateAssetPhoto(id, { assetId: id, photoUrl, thumbnailUrl })).json();
    },
    onSuccess: async (_, { photoUrl, thumbnailUrl }: { photoUrl: string | null; thumbnailUrl: string | null }) => {
      queryClient.invalidateQueries({
        queryKey: assetKeys.detail(id, false),
      });
      toast.success(!photoUrl && !thumbnailUrl ? 'Asset photo deleted successfully' : 'Asset photo updated successfully');
    },
    onError: (error) => {
      toast.error(`Error updating asset photo: ${error.message}`);
    },
  });

  const isPending = useMemo(
    () => updateAssetPending || isUpdatingAssetState || setAssetPhotoPending || createAssetCategoryPending,
    [updateAssetPending, isUpdatingAssetState, setAssetPhotoPending, createAssetCategoryPending],
  );

  const form = useForm<z.infer<typeof extendedAssetFormSchema>>({
    resolver: zodResolver(extendedAssetFormSchema),
    defaultValues: {
      class: '',
      comments: '',
      engineNumber: '',
      id: '',
      rate: '',
      internalId: '',
      manufacturer: '',
      model: '',
      registration: '',
      serialNumber: '',
      vin: '',
      locationId: '',
      operatedBy: null,
      type: undefined,
      categoryId: '',
      tags: [],
      inspectionFormId: '',
      serviceFormId: '',
      issueFormId: '',
      year: '',
    },
  });
  const { reset, formState } = form;

  async function handleImageImport(file: File) {
    const timestamp = Date.now();
    const baseFilename = `${timestamp}-${file.name}`;
    const thumbnailFilename = `thumbnail-${baseFilename}`;

    // Generate presigned URLs for both photo and thumbnail
    const [{ upload: photoUploadTarget, fileUrl: photoUrl }, { upload: thumbnailUploadTarget, fileUrl: thumbnailUrl }] = await Promise.all([
      generateAssetPhotoPresignedUploadUrl({
        filename: baseFilename,
        contentType: file.type,
        assetId: id!,
      }),
      generateAssetPhotoPresignedUploadUrl({
        filename: thumbnailFilename,
        contentType: 'image/jpeg', // Thumbnail is always JPEG
        assetId: id!,
      }),
    ]);

    // Create thumbnail from the original file using canvas
    const thumbnailFile = await new Promise<File>((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);

        // Calculate dimensions maintaining aspect ratio (max 640px)
        const maxSize = 640;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob and then to File
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create thumbnail blob'));
              return;
            }

            const thumbnailFile = new File([blob], thumbnailFilename, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(thumbnailFile);
          },
          'image/jpeg',
          0.8,
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });

    // Upload both files in parallel
    await Promise.all([
      postToPresignedTarget(photoUploadTarget, file, 'Photo upload'),
      postToPresignedTarget(thumbnailUploadTarget, thumbnailFile, 'Thumbnail upload'),
    ]);

    await updateAssetPhoto({ photoUrl, thumbnailUrl });
  }

  async function handleImageDelete() {
    await updateAssetPhoto({ photoUrl: null, thumbnailUrl: null });
  }

  async function handleDelete() {
    await bulkUpdateAssetState({ state: RecordState.Deleted });
  }

  async function handleArchive() {
    await bulkUpdateAssetState({ state: RecordState.Archived });
  }

  async function handleRestore() {
    await bulkUpdateAssetState({ state: RecordState.Active });
  }

  async function handleSubmit(data: z.infer<typeof extendedAssetFormSchema>) {
    const categoryIdIsValidUUID = z.string().uuid().safeParse(data.categoryId).success;

    if (data.categoryId && !categoryIdIsValidUUID) {
      const category = await createAssetCategory(data);
      await updateAsset({ ...data, categoryId: category.id });
      reset({ ...data, categoryId: category.id });
    } else {
      await updateAsset(data);
      reset(data);
    }
  }

  const resetFormToOriginal = useCallback(() => {
    if (!asset) return;

    reset({
      class: asset.class || '',
      comments: asset.comments || '',
      engineNumber: asset.engineNumber || '',
      id: asset.id || '',
      internalId: asset.internalId || '',
      manufacturer: asset.manufacturer || '',
      model: asset.model || '',
      registration: asset.registration || '',
      serialNumber: asset.serialNumber || '',
      vin: asset.chassisVin || '',
      rate: asset.rate != null ? String(asset.rate) : '',
      locationId: asset.location?.id || '',
      operatedBy: asset.operatedBy
        ? { label: EntityRefUtil.createUserFullNameRef(asset.operatedBy), value: asset.operatedBy.id, avatarUrl: asset.operatedBy.avatarUrl ?? undefined }
        : null,
      type: (asset.type as GearbeltAssetType) || undefined,
      categoryId: asset.category?.id || '',
      tags: asset.tags?.map((tag) => ({ label: tag.tag, value: tag.tag })) || [],
      year: asset.year ? asset.year.toString() : undefined,
    });
  }, [asset, reset]);

  useEffect(() => {
    if (asset && manufacturers) {
      resetFormToOriginal();
    }
  }, [resetFormToOriginal, asset, manufacturers]);

  const getUniqueFormsByTypeLocal = useCallback((forms: GetAssetByIdResponseDTO['asset']['forms'] | undefined, formType: FormType): ProcessedForm[] => {
    return getUniqueFormsByType(forms, formType);
  }, []);

  const getProcessedFormsLocal = useCallback(
    (formType: FormType.RepairForm | FormType.ScheduleForm): ProcessedForm[] => {
      return getProcessedForms(asset?.forms, formType);
    },
    [asset?.forms],
  );

  // Get plan type and schedule limit from account service
  const { data: account } = useQuery(getSitemateStartAccountQueryOptions({ accountId: org?.accountId }));
  const planType = account?.billing?.gearbelt?.planType as GearbeltPlanType | undefined;
  const schedulePlanLimit = getSchedulePlanLimit(planType);

  const getProcessedSchedulesLocal = useCallback((): ProcessedSchedule[] => {
    return getProcessedSchedules(asset?.schedules, schedulePlanLimit);
  }, [asset?.schedules, schedulePlanLimit]);

  const inspectionForms = useMemo(() => {
    const forms = getUniqueFormsByTypeLocal(asset?.forms, FormType.InspectionForm);
    if (!forms.length) return [];
    return forms;
  }, [getUniqueFormsByTypeLocal, asset?.forms]);
  const repairForms = useMemo(() => getProcessedFormsLocal(FormType.RepairForm), [getProcessedFormsLocal]);
  const schedules = useMemo(() => getProcessedSchedulesLocal(), [getProcessedSchedulesLocal]);

  if (isLoading || !isImageLoaded) return <AssetOverviewSkeleton />;

  return (
    <div className="flex w-full flex-col gap-[20px] p-4 pb-6 min-[1440px]:flex-row">
      <Form {...form}>
        <form
          className="flex w-full flex-col min-[1440px]:flex-row min-[1440px]:items-start min-[1440px]:gap-[20px]"
          onSubmit={form.handleSubmit(handleSubmit)}>
          <fieldset disabled={!canManageAssets} className="contents">
            <div className="flex w-full flex-col gap-[20px] min-[1440px]:w-auto min-[1440px]:max-w-[440px] min-[1920px]:max-w-[680px]" id="left">
              <div className="flex flex-col gap-[20px] sm:flex-row">
                <div className="h-[360px] w-full rounded-[10px] bg-white p-[20px] min-[1440px]:h-[210px] min-[1440px]:w-[210px] min-[1440px]:flex-none min-[1920px]:h-[325px] min-[1920px]:w-[325px] sm:flex-1">
                  {asset?.thumbnailUrl ? (
                    <div className="group relative flex h-full w-full items-center justify-center">
                      <img src={asset.thumbnailUrl} alt="Asset thumbnail" className="absolute h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-black opacity-0 transition-opacity group-hover:opacity-50" />
                      {canManageAssets && (
                        <button
                          type="button"
                          aria-label="Delete asset photo"
                          onClick={handleImageDelete}
                          disabled={setAssetPhotoPending}
                          className="hover:bg-grey-1 relative z-10 flex items-center justify-center rounded-[5px] bg-white opacity-0 shadow-md transition-opacity group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-50">
                          <DeleteIcon size={30} className="text-default-red" />
                        </button>
                      )}
                    </div>
                  ) : setAssetPhotoPending ? (
                    <div className="border-grey-4 flex h-full w-full items-center justify-center border-2 border-dashed">
                      <div className="flex flex-col items-center gap-2">
                        <div className="border-default-blue size-6 animate-spin rounded-full border-2 border-t-transparent" />
                        <span className="text-grey-5">Uploading...</span>
                      </div>
                    </div>
                  ) : (
                    <Dropzone
                      accept={{
                        'image/jpeg': ['.jpg', '.jpeg'],
                        'image/png': ['.png'],
                      }}
                      maxSize={ASSET_PHOTO_MAX_FILE_SIZE_BYTES}
                      maxFiles={1}
                      disabled={setAssetPhotoPending || !canManageAssets}
                      onDropAccepted={(files) => handleImageImport(files[0])}
                      onDropRejected={(fileRejections) => {
                        const rejection = fileRejections[0];
                        if (rejection.errors.some((e) => e.code === 'file-invalid-type')) {
                          toast.error('File type is not supported.');
                        } else if (rejection.errors.some((e) => e.code === 'file-too-large')) {
                          toast.error('File exceeds 2MB.');
                        } else {
                          toast.error('File is invalid. Please check the file and try again.');
                        }
                      }}>
                      <DropzoneContent className={cn('border-grey-4 flex h-full w-full items-center justify-center border-2 border-dashed p-4 text-center')}>
                        <DropzoneTitle>
                          <CameraIcon className="text-grey-5 size-8" />
                        </DropzoneTitle>
                        <DropzoneDescription className="text-grey-5">Drag photo here or click to upload *</DropzoneDescription>
                        <DropzoneInput />
                      </DropzoneContent>
                    </Dropzone>
                  )}
                </div>
                <div className="h-[360px] w-full rounded-[10px] bg-white p-[20px] min-[1440px]:h-[210px] min-[1440px]:w-[210px] min-[1440px]:flex-none min-[1920px]:h-[325px] min-[1920px]:w-[325px] sm:flex-1">
                  {asset?.qrCodes?.[0]?.value && (
                    <div className="group relative flex h-full w-full items-center justify-center">
                      <QRCodeSVG
                        value={getAssetQrUrl(asset.qrCodes[0].value)}
                        size={240}
                        imageSettings={{ width: 64, height: 64, excavate: true, src: qrLogo }}
                        level="H"
                        className="absolute h-full w-full"
                      />
                      <div className="absolute inset-0 bg-black opacity-0 transition-opacity group-hover:opacity-50" />
                      <button
                        type="button"
                        aria-label="Download QR code"
                        onClick={handleQRCodeDownload}
                        className="hover:bg-grey-1 relative z-10 flex items-center justify-center rounded-[5px] bg-white opacity-0 shadow-md transition-opacity group-hover:opacity-100">
                        <DownloadIcon size={30} className="text-default-text" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex w-full flex-col gap-2 rounded-[10px] bg-white p-4">
                <span className="text-xl font-medium">Unit Readings</span>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {units?.map((unit) => {
                    const reading = asset?.unitReadings?.find((reading) => reading.unitId === unit.id);

                    return (
                      <Fragment key={unit.id}>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger type="button">
                              <div className="flex flex-col gap-1">
                                <div className="flex justify-start">
                                  <Label className="text-sm">Last Reading {unit.name}</Label>
                                </div>
                                <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 text-sm ring-2 ring-inset">
                                  {reading ? reading.reading.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '0'} {unit.abbreviation}
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipPortal>
                              <TooltipContent side="top" sideOffset={0} className="bg-default-text p-1 text-xs text-white">
                                <TooltipArrow className="fill-default-text" />
                                <Link className="hover:underline" to={getWorkspaceRoute(`/assets/${id}/reading-logs`)}>
                                  To edit readings, navigate to Reading Logs section
                                </Link>
                              </TooltipContent>
                            </TooltipPortal>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider delayDuration={0}>
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger type="button">
                              <div className="flex flex-col gap-1">
                                <div className="flex justify-start">
                                  <Label className="text-sm">Last Reading {unit.name} At</Label>
                                </div>
                                <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 text-sm ring-2 ring-inset">
                                  {(() => {
                                    const parsedValue = reading ? new Date(reading.recordedAt ?? reading.createdAt) : null;

                                    if (!parsedValue) return <span className="text-secondary-text">Never</span>;

                                    let status: 'danger' | 'warning' | 'success' = 'danger';
                                    if (subDays(startOfDay(new Date()), 3).getTime() < parsedValue.getTime()) status = 'warning';
                                    if (startOfDay(new Date()).getTime() < parsedValue.getTime()) status = 'success';

                                    const timeAgo = isToday(parsedValue) ? 'Today' : formatDistanceToNow(parsedValue);

                                    const icon = match(status)
                                      .with('success', () => <CheckCircleIcon className="text-default-green" />)
                                      .with('warning', () => <WarningTriangleIcon className="text-default-orange" />)
                                      .otherwise(() => <ExclamationIcon className="text-default-red" />);

                                    return (
                                      <span className="flex items-center gap-1">
                                        {icon} {timeAgo}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipPortal>
                              <TooltipContent side="top" sideOffset={0} className="bg-default-text p-1 text-xs text-white">
                                <TooltipArrow className="fill-default-text" />
                                <Link className="hover:underline" to={getWorkspaceRoute(`/assets/${id}/reading-logs`)}>
                                  To edit readings, navigate to Reading Logs section
                                </Link>
                              </TooltipContent>
                            </TooltipPortal>
                          </Tooltip>
                        </TooltipProvider>
                      </Fragment>
                    );
                  })}
                </div>
              </div>
              <div className="flex w-full flex-col gap-2 rounded-[10px] bg-white p-4">
                <span className="text-xl font-medium">Deployment</span>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm">Inspection Forms</span>

                    <div className="flex min-h-[45px] flex-wrap items-start gap-1 p-2 pt-0 pl-0">
                      {inspectionForms.length > 0 ? (
                        inspectionForms.map((form, index) => (
                          <FormDisplay
                            key={form.id}
                            form={form}
                            allAssetForms={asset?.forms || []}
                            isSecondary={index >= FORM_TYPE_LIMIT[GlobalFormType.InspectionForm]}
                          />
                        ))
                      ) : (
                        <span className="text-grey-5 text-sm">-</span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm">Repair Forms</span>

                      <div className="flex min-h-[45px] flex-wrap items-start gap-1 p-2 pt-0 pl-0">
                        {repairForms.length > 0 ? (
                          repairForms.map((form) => <FormDisplay key={form.id} form={form} allAssetForms={asset?.forms || []} isSecondary={!form.isPrimary} />)
                        ) : (
                          <span className="text-grey-5 text-sm">-</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm">Schedules</span>

                      <div className="flex min-h-[45px] flex-wrap items-start gap-1 p-2 pt-0 pl-0">
                        {schedules.length > 0 ? (
                          schedules.map((schedule) => (
                            <ScheduleDisplay
                              key={schedule.id}
                              schedule={schedule}
                              allAssetSchedules={asset?.schedules || []}
                              isSecondary={!schedule.isPrimary}
                              planLimit={schedulePlanLimit}
                              planType={planType}
                              assetType={asset?.type as GearbeltAssetType}
                            />
                          ))
                        ) : (
                          <span className="text-grey-5 text-sm">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
          <div
            className="mt-[20px] flex w-full flex-col gap-2 rounded-[10px] bg-white p-4 min-[1440px]:mt-[0px] min-[1440px]:min-w-[440px] min-[1920px]:min-w-[680px]"
            id="right">
            <span className="text-xl font-medium">Details</span>
            <fieldset disabled={!canManageAssets} className="contents">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Controller
                  control={form.control}
                  name="locationId"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Last Location</Label>
                      <div className="flex h-full flex-1 flex-col">
                        <Select<{ label: string; value: string }>
                          value={value ? { label: locations?.find((location) => location.id === value)?.name ?? '', value: value } : undefined}
                          onChange={(option) => onChange(option?.value || '')}
                          options={locations?.map((location) => ({ label: location.name, value: location.id })) ?? []}
                          classNames={SELECT_STYLES()}
                          className="w-full"
                          placeholder="Select..."
                          isDisabled={!canManageAssets}
                        />
                      </div>
                    </div>
                  )}
                />
                <div className="flex flex-col gap-1">
                  <div className="flex justify-start">
                    <Label className="text-sm">Last Location At</Label>
                  </div>
                  <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 text-sm ring-2 ring-inset">
                    {(() => {
                      const parsedValue = asset?.assetLocation ? new Date(asset?.assetLocation.createdAt) : null;

                      if (!parsedValue) return <span className="text-secondary-text">Never</span>;

                      let status: 'danger' | 'warning' | 'success' = 'danger';
                      if (subDays(startOfDay(new Date()), 3).getTime() < parsedValue.getTime()) status = 'warning';
                      if (startOfDay(new Date()).getTime() < parsedValue.getTime()) status = 'success';

                      const timeAgo = isToday(parsedValue) ? 'Today' : formatDistanceToNow(parsedValue);

                      const icon = match(status)
                        .with('success', () => <CheckCircleIcon className="text-default-green" />)
                        .with('warning', () => <WarningTriangleIcon className="text-default-orange" />)
                        .otherwise(() => <ExclamationIcon className="text-default-red" />);

                      return (
                        <span className="flex items-center gap-1">
                          {icon} {timeAgo}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:items-stretch">
                <Controller
                  control={form.control}
                  name="operatedBy"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Operator</Label>
                      <div className="flex flex-1 flex-col">
                        <AsyncSelect<UserOption>
                          cacheOptions
                          defaultOptions
                          value={value ?? null}
                          onChange={(option) => onChange(option ?? null)}
                          loadOptions={(input) => loadUserOptions(input, 'sitemate-users', () => toast.error('Failed to load users'))}
                          formatOptionLabel={(option, { context }) => (
                            <UserAvatarLabel
                              value={option.label}
                              operatedByFullInitials={initials(option.label)}
                              avatarUrl={option.avatarUrl ?? ''}
                              avatarBackgroundColorClass={getAvatarBackgroundColorClass(option.label || '')}
                              avatarSize={context === 'menu' ? 'lg' : 'sm'}
                            />
                          )}
                          className="w-full"
                          isClearable
                          placeholder="Select..."
                          isDisabled={!canManageAssets}
                        />
                      </div>
                    </div>
                  )}
                />
                <Controller
                  control={form.control}
                  name="tags"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Tags</Label>
                      <AsyncCreatableMultiSelect<{ label: string; value: string }>
                        defaultOptions
                        loadOptions={async (input: string) => {
                          if (!input) {
                            return tags?.map(({ tag }) => ({ label: tag, value: tag })) || [];
                          }
                          const filtered = tags?.filter(({ tag }) => tag.toLowerCase().includes(input.toLowerCase())) || [];
                          return filtered.map(({ tag }) => ({ label: tag, value: tag }));
                        }}
                        value={value?.map((tag) => ({ label: tag.label, value: tag.value }))}
                        onChange={(newValue) => onChange(newValue || [])}
                        classNames={MULTI_SELECT_STYLES()}
                        className="h-full !items-start"
                        placeholder="Select..."
                        isDisabled={!canManageAssets}
                      />
                    </div>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Controller
                  control={form.control}
                  name="internalId"
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm" required>
                        Asset ID
                      </Label>
                      <Input aria-label="Asset ID" value={value} onChange={onChange} placeholder="Text..." error={!!error} />
                      {error && <div className="text-default-red text-sm">{error.message}</div>}
                    </div>
                  )}
                />
                <Controller
                  control={form.control}
                  name="type"
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm" required>
                        Asset Type
                      </Label>
                      <Select<{ label: string; value: string }>
                        aria-label="Asset Type"
                        value={value ? { label: value, value: value } : undefined}
                        onChange={(option) => onChange(option?.value)}
                        options={Object.values(GearbeltAssetType).map((label) => ({ label, value: label })) ?? []}
                        classNames={SELECT_STYLES(!!error)}
                        className="w-full"
                        placeholder="Text..."
                        isDisabled={!canManageAssets}
                      />
                      {error && <div className="text-default-red text-sm">{error.message}</div>}
                    </div>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Controller
                  control={form.control}
                  name="categoryId"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Asset Category</Label>
                      <AsyncCreatableSelect<{ label: string; value: string }>
                        cacheOptions
                        defaultOptions
                        value={
                          value
                            ? assetCategories?.find((cat) => cat.id === value)
                              ? { label: assetCategories.find((cat) => cat.id === value)!.name, value: value }
                              : { label: value, value: value }
                            : undefined
                        }
                        onChange={(option) => onChange(option?.value || '')}
                        loadOptions={async (input: string) => {
                          if (!input) {
                            return assetCategories?.map((category) => ({ label: category.name, value: category.id })) ?? [];
                          }
                          const filtered = assetCategories?.filter((cat) => cat.name.toLowerCase().includes(input.toLowerCase())) || [];
                          return filtered.map((category) => ({ label: category.name, value: category.id }));
                        }}
                        className="w-full"
                        isClearable
                        placeholder="Select..."
                        isDisabled={!canManageAssets}
                      />
                    </div>
                  )}
                />
                <Controller
                  control={form.control}
                  name="manufacturer"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Manufacturer</Label>
                      <AsyncCreatableSelect<{ label: string; value: string }>
                        cacheOptions
                        defaultOptions
                        value={value ? { label: value, value: value } : undefined}
                        onChange={(option) => onChange(option?.value || '')}
                        loadOptions={async (input: string) => {
                          const options = [
                            ...(manufacturers?.map((manufacturer) => ({ label: manufacturer.make, value: manufacturer.make })) ?? []),
                            ...(asset?.manufacturer && !manufacturers?.some((m) => m.make === asset.manufacturer)
                              ? [{ label: asset.manufacturer, value: asset.manufacturer }]
                              : []),
                          ].sort((a, b) => a.label.localeCompare(b.label));

                          if (!input) {
                            return options;
                          }
                          return options.filter((opt) => opt.label.toLowerCase().includes(input.toLowerCase()));
                        }}
                        className="w-full"
                        isClearable
                        placeholder="Select..."
                        isDisabled={!canManageAssets}
                      />
                    </div>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Controller
                  control={form.control}
                  name="engineNumber"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Engine Number</Label>
                      <Input value={value} onChange={onChange} placeholder="Text..." />
                    </div>
                  )}
                />
                <Controller
                  control={form.control}
                  name="model"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Model</Label>
                      <Input aria-label="Model" value={value} onChange={onChange} placeholder="Text..." />
                    </div>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Controller
                  control={form.control}
                  name="class"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Class</Label>
                      <Input value={value} onChange={onChange} placeholder="Text..." />
                    </div>
                  )}
                />
                <Controller
                  control={form.control}
                  name="year"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Year</Label>
                      <Select<{ label: string; value: string }>
                        value={value ? { label: value.toString(), value: value.toString() } : undefined}
                        onChange={(option) => onChange(option?.value || '')}
                        options={Array.from({ length: new Date().getFullYear() - 1899 }, (_, i) => 1900 + i)
                          .reverse()
                          .map((year) => ({
                            label: year.toString(),
                            value: year.toString(),
                          }))}
                        classNames={SELECT_STYLES()}
                        className="w-full"
                        isClearable
                        placeholder="Select..."
                        isDisabled={!canManageAssets}
                      />
                    </div>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Controller
                  control={form.control}
                  name="registration"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Registration</Label>
                      <Input value={value} onChange={onChange} placeholder="Text..." />
                    </div>
                  )}
                />
                <Controller
                  control={form.control}
                  name="serialNumber"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Serial Number</Label>
                      <Input aria-label="Serial Number" value={value} onChange={onChange} placeholder="Text..." />
                    </div>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Controller
                  control={form.control}
                  name="vin"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Chassis/VIN</Label>
                      <Input value={value} onChange={onChange} placeholder="Text..." />
                    </div>
                  )}
                />
                <Controller
                  control={form.control}
                  name="rate"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm">Rate</Label>
                      <Input
                        value={value}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || /^\d*\.?\d{0,4}$/.test(val)) {
                            onChange(e);
                          }
                        }}
                        placeholder="Number..."
                      />
                    </div>
                  )}
                />
              </div>
            </fieldset>
            <div className="flex flex-col gap-1">
              <Label className="text-sm">Asset URL</Label>
              {assetQrUrl ? (
                <div className="flex h-[41px] items-stretch">
                  <div className="border-grey-3 bg-grey-1 text-default-text flex min-w-0 flex-1 items-center rounded-l border-2 p-2 text-sm">
                    <span className="truncate">{assetQrUrl}</span>
                  </div>
                  <button
                    type="button"
                    aria-label="Copy asset URL"
                    onClick={handleCopyAssetUrl}
                    className="border-grey-3 hover:bg-grey-1 flex items-center rounded-r border-2 border-l-0 bg-white p-2">
                    <CloneIcon size={20} className="text-default-text" />
                  </button>
                </div>
              ) : (
                <span className="text-secondary-text text-sm">-</span>
              )}
            </div>
            <fieldset disabled={!canManageAssets} className="contents">
              <Controller
                control={form.control}
                name="comments"
                render={({ field: { value, onChange } }) => (
                  <div className="flex flex-col gap-1">
                    <Label className="text-sm">Comments</Label>
                    <Textarea value={value} onChange={onChange} placeholder="Text..." rows={4} />
                  </div>
                )}
              />
            </fieldset>
            <div className="flex justify-between gap-2">
              <div className="flex gap-2">
                {canManageAssets && (
                  <Button variant="success" disabled={isPending || !formState.isDirty} loading={updateAssetPending}>
                    Save
                  </Button>
                )}
              </div>

              {canManageAssets && asset?.state === 'active' && (
                <Button variant="outline" icon={<ArchiveIcon />} type="button" disabled={isPending} loading={isUpdatingAssetState} onClick={handleArchive}>
                  Archive
                </Button>
              )}

              {canManageAssets && asset?.state === 'archived' && (
                <div className="flex gap-2">
                  <Button variant="outline" icon={<UndoIcon />} type="button" disabled={isPending} loading={isUpdatingAssetState} onClick={handleRestore}>
                    Restore
                  </Button>
                  <Button variant="destructive" icon={<DeleteIcon />} type="button" disabled={isPending} loading={isUpdatingAssetState} onClick={handleDelete}>
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </div>
        </form>
      </Form>

      <QRPrintDocument type="assets" contentRef={printQrRef} qrs={assetQRCodes} />
    </div>
  );
}

function AssetOverviewSkeleton() {
  return (
    <div className="flex w-full flex-col gap-[20px] p-4 pb-6 min-[1440px]:flex-row min-[1440px]:items-start">
      <div className="flex w-full flex-col gap-[20px] min-[1440px]:w-auto min-[1440px]:max-w-[440px] min-[1920px]:max-w-[680px]" id="left">
        <div className="flex flex-col gap-[20px] sm:flex-row">
          <Skeleton className="h-[360px] w-full rounded-[10px] bg-white p-[20px] min-[1440px]:h-[210px] min-[1440px]:w-[210px] min-[1440px]:flex-none min-[1920px]:h-[325px] min-[1920px]:w-[325px] sm:flex-1" />
          <Skeleton className="h-[360px] w-full rounded-[10px] bg-white p-[20px] min-[1440px]:h-[210px] min-[1440px]:w-[210px] min-[1440px]:flex-none min-[1920px]:h-[325px] min-[1920px]:w-[325px] sm:flex-1" />
        </div>
        <div className="flex w-full flex-col gap-2 rounded-[10px] bg-white p-4">
          <Skeleton className="h-[40px] w-[100px]" />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {Array.from({ length: 2 }).map((_, index) => (
              <Skeleton key={index} className="h-[65px] w-full" />
            ))}
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 rounded-[10px] bg-white p-4">
          <Skeleton className="h-[40px] w-[100px]" />
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="flex flex-col gap-2">
              <div className="flex flex-col gap-2">
                <Skeleton className="h-[20px] w-[100px]" />
                <Skeleton className="h-[65px] w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        className="mt-[20px] flex w-full flex-col gap-2 rounded-[10px] bg-white p-4 min-[1440px]:mt-[0px] min-[1440px]:min-w-[440px] min-[1920px]:min-w-[680px]"
        id="right">
        <Skeleton className="h-[40px] w-[100px]" />
        {Array.from({ length: 8 }).map((_, idx) => (
          <div key={idx} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {Array.from({ length: 2 }).map((_, index) => (
              <Skeleton key={`${idx}-${index}`} className="h-[65px] w-full" />
            ))}
          </div>
        ))}
        <Skeleton className="h-[120px]" />
      </div>
    </div>
  );
}

type ProcessedForm = NonNullable<GetAssetByIdResponseDTO['asset']['forms']>[number] & { isPrimary?: boolean };

function FormDisplay({
  form,
  allAssetForms,
  isSecondary,
}: {
  form: ProcessedForm;
  allAssetForms: NonNullable<GetAssetByIdResponseDTO['asset']['forms']>;
  isSecondary?: boolean;
}) {
  const deploymentRules = useMemo(() => {
    return allAssetForms
      .filter((f) => f.id === form.id && f.deploymentRule)
      .map((f) => f.deploymentRule!)
      .filter((rule, index, self) => index === self.findIndex((r) => r.id === rule.id))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()); // Sort newest first
  }, [allAssetForms, form.id]);

  const displayRules = deploymentRules.slice(0, 3);
  const remainingRules = deploymentRules.length - 3;

  const formTypeText = match(form.type)
    .with(FormType.InspectionForm, () => 'inspection')
    .with(FormType.RepairForm, () => 'repair')
    .with(FormType.ScheduleForm, () => 'schedule')
    .otherwise(() => 'unknown');

  const formTypeLimit = FORM_TYPE_LIMIT[form.type as GlobalFormType];

  return (
    <TooltipProvider key={form.id}>
      <Tooltip delayDuration={0}>
        <TooltipTrigger type="button" className="max-w-full">
          <FormBadge className={cn(isSecondary && 'relative top-[2px]')} form={form} disabled={isSecondary} truncate />
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent side="bottom" sideOffset={0} className="bg-default-text max-w-[200px] border-none p-1 text-xs text-white">
            <TooltipArrow className="fill-default-text" />
            {deploymentRules.length > 0 ? (
              <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                {isSecondary && (
                  <div>
                    <b>Warning:</b> This {formTypeText} form cannot be used because only {formTypeLimit} {formTypeText} form{formTypeLimit > 1 ? 's' : ''} can
                    be deployed to each asset.
                  </div>
                )}

                <div>
                  <b>Rules:</b>
                  <ul className="list-disc pl-4">
                    {displayRules.map((rule) => (
                      <li key={rule.id} className="hover:underline">
                        <Link className="block w-full truncate" to={getWorkspaceRoute(`/deployments/${rule.id}`)}>
                          {rule.name}
                        </Link>
                      </li>
                    ))}
                    {remainingRules > 0 && <li>+{remainingRules} more...</li>}
                  </ul>
                </div>
              </div>
            ) : (
              <span>No deployment rules</span>
            )}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}

type AllAssetSchedulesType = NonNullable<GetAssetByIdResponseDTO['asset']['schedules']>;

function ScheduleDisplay({
  schedule,
  allAssetSchedules,
  isSecondary,
  planLimit,
  planType,
  assetType,
}: {
  schedule: ProcessedSchedule;
  allAssetSchedules: AllAssetSchedulesType;
  isSecondary?: boolean;
  planLimit?: number;
  planType?: GearbeltPlanType;
  assetType?: GearbeltAssetType;
}) {
  const deploymentRules = useMemo(() => {
    return allAssetSchedules
      .filter((s) => s.id === schedule.id && s.deploymentRule)
      .map((s) => s.deploymentRule!)
      .filter((rule, index, self) => index === self.findIndex((r) => r.id === rule.id))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [allAssetSchedules, schedule.id]);

  const displayRules = deploymentRules.slice(0, 3);
  const remainingRules = deploymentRules.length - 3;

  const hasUnitBasedWarning = assetType === GearbeltAssetType.ToolsAndEquipment && 'hasUnitBasedTrigger' in schedule && schedule.hasUnitBasedTrigger;
  const showWarning = isSecondary || hasUnitBasedWarning;

  return (
    <TooltipProvider key={schedule.id}>
      <Tooltip delayDuration={0}>
        <TooltipTrigger type="button">
          <ScheduleBadge className="gap-[2px]" schedule={schedule} disabled={showWarning} />
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent side="bottom" sideOffset={0} className="bg-default-text max-w-[200px] border-none p-1 text-xs text-white">
            <TooltipArrow className="fill-default-text" />
            {deploymentRules.length > 0 ? (
              <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                {isSecondary && (
                  <div>
                    <b>Warning:</b> {getScheduleLimitWarning(planLimit, planType)}
                  </div>
                )}

                {hasUnitBasedWarning && (
                  <div>
                    <b>Warning:</b> Schedules with custom units are not allowed for Tools & Equipment.
                  </div>
                )}

                <div>
                  <b>Rules:</b>
                  <ul className="list-disc pl-4">
                    {displayRules.map((rule) => (
                      <li key={rule.id} className="hover:underline">
                        <Link className="block w-full truncate" to={getWorkspaceRoute(`/deployments/${rule.id}`)}>
                          {rule.name}
                        </Link>
                      </li>
                    ))}
                    {remainingRules > 0 && <li>+{remainingRules} more...</li>}
                  </ul>
                </div>
              </div>
            ) : (
              <span>No deployment rules</span>
            )}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}
